import { useState, useMemo } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module6.css'

const money = (v) => `$${Math.round(v || 0).toLocaleString()}`
const INVESTOR_CATALOG = [
  { name: 'Investor A', amount: 500000 },
  { name: 'Investor B', amount: 400000 },
  { name: 'Investor C', amount: 300000 },
  { name: 'Investor D', amount: 250000 },
  { name: 'Investor E', amount: 200000 },
  { name: 'Investor F', amount: 175000 },
  { name: 'Investor G', amount: 150000 },
  { name: 'Investor H', amount: 125000 },
  { name: 'Investor I', amount: 100000 },
  { name: 'Investor J', amount: 90000 },
  { name: 'Investor K', amount: 80000 },
  { name: 'Investor L', amount: 75000 },
  { name: 'Investor M', amount: 70000 },
  { name: 'Investor N', amount: 65000 },
  { name: 'Investor O', amount: 60000 },
  { name: 'Investor P', amount: 55000 },
  { name: 'Investor Q', amount: 50000 },
  { name: 'Investor R', amount: 45000 },
  { name: 'Investor S', amount: 40000 },
  { name: 'Investor T', amount: 35000 },
]

export default function Module7Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()

  const raw = dealState.rawDealData || {}
  const uw = dealState.underwritingMetrics || {}
  const lm = dealState.lenderMatches || {}
  const top = lm.topRecommendation || {}
  const savedSyndicate = dealState.syndicateData || {}

  const propertyPrice = Number(raw.price) || 0
  const loanAmount = Number(top.loanAmount || uw.debtService?.loanAmount) || propertyPrice * 0.70
  const equityGap = Math.max(0, propertyPrice - loanAmount)
  const loanPct = propertyPrice > 0 ? (loanAmount / propertyPrice) * 100 : 70
  const equityGapPct = propertyPrice > 0 ? (equityGap / propertyPrice) * 100 : 30

  const [showSyndicatePrompt, setShowSyndicatePrompt] = useState(
    () => !savedSyndicate.syndicationEnabled && !dealState.syndicateWanted
  )

  const [minInvestment, setMinInvestment] = useState(savedSyndicate.minInvestment || 50000)
  const [profitShare, setProfitShare] = useState(savedSyndicate.profitShare || 70)
  const [targetReturn, setTargetReturn] = useState(savedSyndicate.targetReturn || 8)
  const [investors, setInvestors] = useState(() =>
    savedSyndicate.investors?.length
      ? savedSyndicate.investors.map((savedInvestor, index) => {
          const catalogInvestor = INVESTOR_CATALOG.find(investor => investor.name === savedInvestor.name)
          return {
            id: savedInvestor.id || index + 1,
            name: savedInvestor.name,
            amount: catalogInvestor?.amount || savedInvestor.amount,
          }
        })
      : []
  )
  const [investorListOpen, setInvestorListOpen] = useState(false)
  const [fundingAlert, setFundingAlert] = useState('')
  const [nextInvId, setNextInvId] = useState(() => {
    const savedIds = savedSyndicate.investors?.map(investor => Number(investor.id) || 0) || []
    return Math.max(0, ...savedIds) + 1
  })

  const totalInvFunds = useMemo(() => investors.reduce((s, inv) => s + (Number(inv.amount) || 0), 0), [investors])
  const invEquityPct = propertyPrice > 0 ? (totalInvFunds / propertyPrice) * 100 : 0
  const stillNeeded = Math.max(0, equityGap - totalInvFunds)
  const totalCapital = loanAmount + totalInvFunds
  const fundingPct = equityGap > 0 ? Math.min(100, (totalInvFunds / equityGap) * 100) : 100
  const activeInvestors = investors.filter(inv => Number(inv.amount) > 0).length
  const gapBarPct = Math.max(0, equityGapPct - invEquityPct)

  const calcOwnership = (amount) => {
    if (!equityGap || !amount) return 0
    return Math.min(100, (Number(amount) / equityGap) * profitShare)
  }
  const calcAnnualROI = (amount) => {
    if (!amount) return 0
    return Number(amount) * (targetReturn / 100)
  }
  const persistSyndicationDraft = (nextInvestors) => {
    const nextTotal = nextInvestors.reduce((sum, investor) => sum + (Number(investor.amount) || 0), 0)
    const nextFundingPct = equityGap > 0 ? Math.min(100, (nextTotal / equityGap) * 100) : 100
    updateDealState({
      syndicateDecisionMade: true,
      syndicateWanted: true,
      syndicateSkipped: false,
      syndicateData: {
        ...savedSyndicate,
        syndicationEnabled: true,
        equityGap,
        loanAmount,
        minInvestment,
        profitShare,
        targetReturn,
        investors: nextInvestors,
        totalInvFunds: nextTotal,
        fundingPct: nextFundingPct,
        updatedAt: new Date().toISOString(),
      },
    })
    updateAvailableModules()
  }
  const handleEnableSyndication = () => {
    setShowSyndicatePrompt(false)
    persistSyndicationDraft(investors)
  }
  const handleSkipSyndication = () => {
    updateDealState({
      syndicateDecisionMade: true,
      syndicateWanted: false,
      syndicateSkipped: true,
      syndicateData: {
        ...savedSyndicate,
        syndicationEnabled: false,
        skippedAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
      },
      pipelineStage: Math.max(dealState.pipelineStage || 1, 8),
      pipelineStatus: 'in_progress',
    })
    updateAvailableModules()
    forceNavigateToModule(8)
  }
  const handleToggleInvestor = (selectedInvestor) => {
    if (!selectedInvestor) return
    const existingInvestor = investors.find(investor => investor.name === selectedInvestor.name)
    if (existingInvestor) {
      handleRemoveInvestor(existingInvestor.id)
      return
    }
    if (fundingPct >= 100) {
      setFundingAlert('Funding is already 100% complete. Deselect an investor before choosing another one.')
      return
    }
    const nextInvestors = [...investors, { id: nextInvId, ...selectedInvestor }]
    setInvestors(nextInvestors)
    persistSyndicationDraft(nextInvestors)
    setNextInvId(id => id + 1)
  }
  const handleRemoveInvestor = (id) => {
    const nextInvestors = investors.filter(investor => investor.id !== id)
    setInvestors(nextInvestors)
    persistSyndicationDraft(nextInvestors)
  }
  const availableInvestors = INVESTOR_CATALOG.filter(
    catalogInvestor => !investors.some(investor => investor.name === catalogInvestor.name)
  )

  function handleProceed() {
    updateDealState({
      syndicateDecisionMade: true,
      syndicateWanted: true,
      syndicateSkipped: false,
      syndicateData: {
        ...savedSyndicate,
        syndicationEnabled: true,
        equityGap,
        loanAmount,
        minInvestment,
        profitShare,
        targetReturn,
        investors: investors.filter(inv => Number(inv.amount) > 0),
        totalInvFunds,
        fundingPct,
        completedAt: new Date().toISOString(),
      },
      pipelineStage: Math.max(dealState.pipelineStage || 1, 8),
      pipelineStatus: 'in_progress',
    })
    updateAvailableModules()
    setTimeout(() => forceNavigateToModule(8), 500)
  }

  if (showSyndicatePrompt) {
    return (
      <div className="synd-prompt-overlay">
        <div className="synd-prompt-card">
          <div className="synd-prompt-icon">🤝</div>
          <h2 className="synd-prompt-title">Deal Syndication Engine</h2>
          <p className="synd-prompt-sub">Do you need support syndicating this deal with co-investors?</p>

          <div className="synd-prompt-info">
            <div className="synd-info-row"><span className="synd-info-icon">💰</span><div><strong>Equity Gap Coverage</strong><span>Pool capital from multiple investors to bridge your equity gap — minimum tickets from $25K.</span></div></div>
            <div className="synd-info-row"><span className="synd-info-icon">📊</span><div><strong>Ownership Allocation</strong><span>Automatically calculate pro-rata ownership and profit share for each co-investor.</span></div></div>
            <div className="synd-info-row"><span className="synd-info-icon">📈</span><div><strong>Projected Returns</strong><span>Show investors their annual ROI and IRR targets based on your underwriting model.</span></div></div>
            <div className="synd-info-row"><span className="synd-info-icon">📄</span><div><strong>Investor Summary</strong><span>Generate a co-investor overview ready to share with your syndicate group.</span></div></div>
          </div>

          <p className="synd-prompt-note">⚡ This module is <strong>optional</strong>. Skip it if you are funding this deal solo or through a single lender.</p>

          <div className="synd-prompt-actions">
            <button className="synd-btn-yes" onClick={handleEnableSyndication}>
              Yes, I Need Syndication Support
            </button>
            <button className="synd-btn-skip" onClick={handleSkipSyndication}>
              Skip — Go to Loan Package Generator
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="m6-page">
      {/* ── Header ── */}
      <div className="m6-header">
        <span className="m6-badge">Module 6</span>
        <div>
          <h1>Deal Syndication Engine</h1>
          <p>Structure co-investor equity, allocate ownership, and bridge the capital gap.</p>
        </div>
        <div className="m6-summary-chip">
          <span>Equity Gap</span>
          <strong>{equityGap > 0 ? money(equityGap) : 'None'}</strong>
          <span>{equityGap > 0 ? `${equityGapPct.toFixed(1)}% of price` : 'Fully Covered'}</span>
        </div>
      </div>

      {equityGap === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', background: '#f0fdf4', borderRadius: 12, margin: '24px 0', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}></div>
          <h2 style={{ color: '#15803d', margin: '0 0 8px' }}>No Equity Gap</h2>
          <p style={{ color: '#166534', margin: '0 0 20px' }}>
            Your approved loan of {money(loanAmount)} covers 100% of the purchase price.
            No co-investor equity is required for this acquisition.
          </p>
          <button className="m6-complete-btn" style={{ background: '#16a34a', borderColor: '#16a34a' }} onClick={handleProceed}>
            Continue to Loan Package →
          </button>
        </div>
      ) : (
        <div className="m6-synd-panel" style={{ marginTop: 16 }}>

          {/* ── Capital Stack ── */}
          <div className="m6-synd-section">
            <div className="m6-synd-section-header">
              <span>Capital Stack</span>
              <strong className={fundingPct >= 100 ? 'm6-synd-funded' : ''}>
                {fundingPct.toFixed(0)}% Raised
              </strong>
            </div>
            <div className="m6-synd-stack-bar">
              <div className="m6-synd-bar-loan" style={{ width: `${loanPct}%` }} title={`Bank Loan ${loanPct.toFixed(1)}%`} />
              <div className="m6-synd-bar-inv" style={{ width: `${Math.min(invEquityPct, equityGapPct)}%` }} title={`Investor Equity ${invEquityPct.toFixed(1)}%`} />
              <div className="m6-synd-bar-gap" style={{ width: `${gapBarPct}%` }} title={`Gap Remaining ${gapBarPct.toFixed(1)}%`} />
            </div>
            <div className="m6-synd-stack-legend">
              <span><em className="m6-leg-loan" />Bank Loan {loanPct.toFixed(1)}%</span>
              <span><em className="m6-leg-inv" />Investor Equity {invEquityPct.toFixed(1)}%</span>
              <span><em className="m6-leg-gap" />Gap Remaining {Math.max(0, equityGapPct - invEquityPct).toFixed(1)}%</span>
            </div>
          </div>

          {/* ── Capital Stats ── */}
          <div className="m6-synd-stats">
            <div><span>Property Price</span><strong>{money(propertyPrice)}</strong></div>
            <div><span>Bank Loan ({loanPct.toFixed(1)}%)</span><strong>{money(loanAmount)}</strong></div>
            <div className="m6-synd-stat-gap"><span>Equity Gap</span><strong>{money(equityGap)}</strong></div>
            <div><span>Investor Funds</span><strong>{money(totalInvFunds)}</strong></div>
            <div className={stillNeeded > 0 ? 'm6-synd-stat-needed' : 'm6-synd-stat-ok'}>
              <span>Still Needed</span><strong>{money(stillNeeded)}</strong>
            </div>
            <div><span>Total Capital</span><strong>{money(totalCapital)}</strong></div>
          </div>

          {/* ── Syndicate Parameters ── */}
          <div className="m6-synd-section">
            <div className="m6-synd-section-header"><span>Syndicate Parameters</span></div>
            <div className="m6-synd-params">
              <div className="m6-synd-param">
                <label>MIN INVESTMENT</label>
                <input type="range" min={10000} max={500000} step={5000} value={minInvestment} onChange={e => setMinInvestment(Number(e.target.value))} className="m6-synd-slider" />
                <strong>{money(minInvestment)}</strong>
              </div>
              <div className="m6-synd-param">
                <label>INVESTOR PROFIT SHARE</label>
                <input type="range" min={30} max={95} step={5} value={profitShare} onChange={e => setProfitShare(Number(e.target.value))} className="m6-synd-slider" />
                <strong>{profitShare}%</strong>
              </div>
              <div className="m6-synd-param">
                <label>TARGET ANNUAL RETURN</label>
                <input type="range" min={4} max={20} step={0.5} value={targetReturn} onChange={e => setTargetReturn(Number(e.target.value))} className="m6-synd-slider" />
                <strong>{targetReturn}%</strong>
              </div>
            </div>
          </div>

          {/* ── Investor Contributions ── */}
          <div className="m6-synd-section">
            <div className="m6-synd-section-header">
              <span>Investor Contributions</span>
              <strong>{activeInvestors} Active Investor{activeInvestors !== 1 ? 's' : ''}</strong>
            </div>
            <div className="m6-synd-inv-table">
              {investors.length > 0 && (
                <div className="m6-synd-inv-head">
                  <span>INVESTOR</span><span>AMOUNT</span><span>OWNERSHIP</span><span>EST. ANNUAL ROI</span><span />
                </div>
              )}
              {investors.map(inv => (
                <div className="m6-synd-inv-row" key={inv.id}>
                  <span className="m6-synd-inv-name">{inv.name}</span>
                  <span className="m6-synd-inv-fixed-amount">{money(inv.amount)}</span>
                  <span className="m6-synd-inv-ownership">{calcOwnership(inv.amount).toFixed(2)}%</span>
                  <span className="m6-synd-inv-roi">{money(Math.round(calcAnnualROI(inv.amount)))}/yr</span>
                  <button className="m6-synd-remove-btn" onClick={() => handleRemoveInvestor(inv.id)} title="Remove">×</button>
                </div>
              ))}
              <div className="m6-synd-add-row">
                <button
                  type="button"
                  className={`m6-synd-investor-bar${investorListOpen ? ' open' : ''}`}
                  onClick={() => setInvestorListOpen(open => !open)}
                >
                  <span>
                    <strong>Available Investor List</strong>
                    <small>{availableInvestors.length} investors available with fixed contribution details</small>
                  </span>
                  <span className="m6-synd-investor-chevron">{investorListOpen ? '▲' : '▼'}</span>
                </button>
              </div>
              {investorListOpen && (
                <div className="m6-synd-investor-dropdown">
                  <div className="m6-synd-investor-option-head">
                    <span>INVESTOR</span>
                    <span>CONTRIBUTION</span>
                    <span>OWNERSHIP</span>
                    <span>EST. ANNUAL ROI</span>
                    <span>STATUS</span>
                  </div>
                  {INVESTOR_CATALOG.map(investor => {
                    const isSelected = investors.some(selectedInvestor => selectedInvestor.name === investor.name)
                    return (
                      <button
                        type="button"
                        className={`m6-synd-investor-option${isSelected ? ' selected' : ''}`}
                        key={investor.name}
                        onClick={() => handleToggleInvestor(investor)}
                      >
                        <strong>{investor.name}</strong>
                        <span>{money(investor.amount)}</span>
                        <span>{calcOwnership(investor.amount).toFixed(2)}%</span>
                        <span>{money(calcAnnualROI(investor.amount))}/yr</span>
                        <span>{isSelected ? 'Deselect' : 'Select'}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Funding Progress ── */}
          <div className="m6-synd-section">
            <div className="m6-synd-section-header">
              <span>Funding Progress</span>
              <strong>{money(totalInvFunds)} of {money(equityGap)} raised</strong>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${fundingPct}%` }} />
            </div>
            <div className="m6-synd-progress-labels">
              <span>{money(0)}</span>
              <span className="m6-synd-pct-label">{fundingPct.toFixed(0)}% of equity gap funded</span>
              <span>{money(equityGap)}</span>
            </div>
          </div>

          {/* ── Advance ── */}
          <div className="module-bottom-advance" style={{ marginTop: 24 }}>
            <button className="m6-complete-btn" onClick={handleProceed}>
              Finalize Syndication &amp; Proceed to Loan Package →
            </button>
          </div>
        </div>
      )}
      {fundingAlert && (
        <div className="m6-synd-alert-overlay" role="presentation" onClick={() => setFundingAlert('')}>
          <div className="m6-synd-alert" role="dialog" aria-modal="true" onClick={event => event.stopPropagation()}>
            <strong>Funding Complete</strong>
            <p>{fundingAlert}</p>
            <button type="button" onClick={() => setFundingAlert('')}>OK</button>
          </div>
        </div>
      )}
    </div>
  )
}
